import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ShoppingCart, Wallet } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { fmtMoney } from "@/lib/dates";

// =============================================================================
// BudgetPoPanel — 28 Oct 2026
//
// James: "I need to see in AP when doing rec if it's linked to a PO — which
// moving forward it should be on most project spend — but what the budget code
// has available and the amount left."
//
// One panel used by both coding screens (the New AP form and the receipts /
// cards CodingCard):
//   PO          every open PO on the job (this supplier's first): number, title,
//               value ex, billed so far, left on the PO. Picking one sets the
//               budget code from the PO.
//   Budget code the job's buckets (v_project_budget_codes): name · $ left. PX
//               only on a project. Codes with no bucket are listed last and
//               say so. Under it: left now → after this bill; red when over;
//               a warning when an overhead (OX/CX) code is on a project bill.
// Muswellbrook's red buckets came from bills coded blind to Fuel and
// Subcontractors — this is the screen where that stops.
// =============================================================================

export interface CodeLite { budget_code: string; description?: string | null; display_name?: string | null; category?: string | null }

export function useJobBuckets(projectId: string | null | undefined) {
  return useQuery({
    queryKey: ["ap_job_buckets", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await (supabase as any).from("v_project_budget_codes")
        .select("budget_code, display_name, current_ex, committed_ex, actual_ex, remaining_ex, unbudgeted")
        .eq("project_id", projectId);
      return (data ?? []) as { budget_code: string; display_name: string; current_ex: number; committed_ex: number; actual_ex: number; remaining_ex: number; unbudgeted: boolean }[];
    },
  });
}

export function useJobPos(projectId: string | null | undefined, excludeInvoiceId?: string | null) {
  return useQuery({
    queryKey: ["ap_job_pos", projectId, excludeInvoiceId ?? null],
    enabled: !!projectId,
    queryFn: async () => {
      const { data: pos } = await (supabase as any).from("purchase_orders")
        .select("po_id, po_number, po_title, supplier_id, budget_code, subtotal_ex_gst, status, finalised_at")
        .eq("project_id", projectId)
        .in("status", ["sent", "acknowledged", "received", "invoiced"])
        .order("po_number", { ascending: false });
      const list = (pos ?? []) as any[];
      if (list.length === 0) return [] as PoPos[];
      const { data: bills } = await (supabase as any).from("ap_invoices")
        .select("po_id, amount_ex_gst, status, ap_invoice_id")
        .in("po_id", list.map(p => p.po_id));
      const billed = new Map<string, number>();
      for (const b of (bills ?? []) as any[]) {
        if (["void", "disputed", "captured"].includes(b.status)) continue;
        if (excludeInvoiceId && b.ap_invoice_id === excludeInvoiceId) continue;
        billed.set(b.po_id, (billed.get(b.po_id) ?? 0) + Number(b.amount_ex_gst ?? 0));
      }
      return list.map(p => ({ ...p, value_ex: Number(p.subtotal_ex_gst ?? 0), billed_ex: billed.get(p.po_id) ?? 0, left_ex: Number(p.subtotal_ex_gst ?? 0) - (billed.get(p.po_id) ?? 0) })) as PoPos[];
    },
  });
}
export interface PoPos { po_id: string; po_number: string; po_title: string | null; supplier_id: string; budget_code: string | null; status: string; finalised_at: string | null; value_ex: number; billed_ex: number; left_ex: number }

const money = (n: number) => fmtMoney(Number(n || 0));

export default function BudgetPoPanel({ projectId, supplierId, invoiceType, codes, budgetCode, onBudgetCode, poId, onPo, amountEx, excludeInvoiceId, compact }: {
  projectId: string | null | undefined;
  supplierId: string | null | undefined;
  invoiceType: "project" | "opex" | "capex";
  codes: CodeLite[];
  budgetCode: string;
  onBudgetCode: (code: string) => void;
  poId: string;
  onPo: (poId: string) => void;
  amountEx: number;
  excludeInvoiceId?: string | null;
  compact?: boolean;
}) {
  const isProject = invoiceType === "project" && !!projectId;
  const { data: buckets = [] } = useJobBuckets(isProject ? projectId : null);
  const { data: pos = [] } = useJobPos(isProject ? projectId : null, excludeInvoiceId);

  const prefix = invoiceType === "project" ? "PX" : invoiceType === "opex" ? "OX" : "CX";
  const byCode = useMemo(() => new Map(buckets.map(b => [b.budget_code, b])), [buckets]);
  const name = (c: string) => { const k = codes.find(x => x.budget_code === c); return k?.description ?? k?.display_name ?? byCode.get(c)?.display_name ?? ""; };

  // the job's buckets first (PX, with money), then PX codes with no bucket, then the current value if odd
  const options = useMemo(() => {
    if (!isProject) return codes.filter(c => c.budget_code.toUpperCase().startsWith(prefix)).map(c => ({ code: c.budget_code, label: `${c.budget_code} — ${name(c.budget_code)}` }));
    const withBucket = buckets.filter(b => !b.unbudgeted && b.budget_code.toUpperCase().startsWith("PX")).sort((a, b) => b.remaining_ex - a.remaining_ex)
      .map(b => ({ code: b.budget_code, label: `${b.budget_code} — ${tidy(b.display_name)} · ${money(b.remaining_ex)} left` }));
    const have = new Set(withBucket.map(o => o.code));
    const rest = codes.filter(c => c.budget_code.toUpperCase().startsWith("PX") && !have.has(c.budget_code))
      .map(c => ({ code: c.budget_code, label: `${c.budget_code} — ${name(c.budget_code)} · no bucket on this job` }));
    return [...withBucket, ...rest];
  }, [isProject, codes, buckets, prefix]);

  const sel = budgetCode ? byCode.get(budgetCode) : undefined;
  const po = poId ? pos.find(p => p.po_id === poId) : undefined;
  const mine = supplierId ? pos.filter(p => p.supplier_id === supplierId) : [];
  const others = pos.filter(p => !mine.includes(p));
  const overhead = isProject && budgetCode && !budgetCode.toUpperCase().startsWith("PX");
  const after = sel ? sel.remaining_ex - amountEx : null;
  const poAfter = po ? po.left_ex - amountEx : null;

  function pickPo(id: string) {
    onPo(id);
    const p = pos.find(x => x.po_id === id);
    if (p?.budget_code) onBudgetCode(p.budget_code);
  }

  const selCls = "w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text";
  return (
    <div className={`space-y-2 ${compact ? "" : "mt-1"}`}>
      {isProject && (
        <div>
          <label className="block text-[11px] text-text-muted mb-1 inline-flex items-center gap-1"><ShoppingCart size={11}/> Against a PO?</label>
          <select value={poId} onChange={e => pickPo(e.target.value)} className={selCls}>
            <option value="">{pos.length === 0 ? "No open POs on this job" : "No PO — spend without a PO"}</option>
            {mine.length > 0 && <optgroup label="This supplier's POs">
              {mine.map(p => <option key={p.po_id} value={p.po_id}>{p.po_number} · {p.po_title ?? ""} · {money(p.value_ex)} · {money(p.left_ex)} left</option>)}
            </optgroup>}
            {others.length > 0 && <optgroup label={mine.length > 0 ? "Other POs on this job" : "POs on this job"}>
              {others.map(p => <option key={p.po_id} value={p.po_id}>{p.po_number} · {p.po_title ?? ""} · {money(p.value_ex)} · {money(p.left_ex)} left</option>)}
            </optgroup>}
          </select>
          {po && (
            <div className={`text-[11px] mt-1 ${poAfter != null && poAfter < -0.5 ? "text-danger" : "text-text-muted"}`}>
              {po.po_number}: {money(po.value_ex)} ex · {money(po.billed_ex)} billed so far · <span className="font-mono text-text">{money(po.left_ex)}</span> left
              {amountEx > 0 && <> → <span className="font-mono">{money(poAfter!)}</span> after this bill{poAfter! < -0.5 && " — over the PO; the extra lands on the bucket as spend"}</>}
              {po.finalised_at && " · PO is finalised"}
            </div>
          )}
          {!po && mine.length > 0 && <div className="text-[11px] text-warn mt-1">This supplier has {mine.length === 1 ? "an open PO" : `${mine.length} open POs`} on this job — link it if this bill is against one.</div>}
        </div>
      )}
      <div>
        <label className="block text-[11px] text-text-muted mb-1 inline-flex items-center gap-1"><Wallet size={11}/> Budget code{isProject && buckets.length > 0 ? " — the job's buckets" : ""}</label>
        <select value={budgetCode} onChange={e => onBudgetCode(e.target.value)} className={`${selCls} font-mono`}>
          <option value="">— pick —</option>
          {budgetCode && !options.some(o => o.code === budgetCode) && <option value={budgetCode}>{budgetCode}{overhead ? " (overhead code)" : ""}</option>}
          {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        {isProject && sel && !sel.unbudgeted && (
          <div className={`text-[11px] mt-1 ${after != null && after < -0.5 ? "text-danger" : "text-text-muted"}`}>
            {tidy(sel.display_name)}: bucket {money(sel.current_ex)} · {money(sel.actual_ex)} spent · {money(sel.committed_ex)} on POs · <span className="font-mono text-text">{money(sel.remaining_ex)}</span> left
            {amountEx > 0 && !po && <> → <span className="font-mono">{money(after!)}</span> after this bill{after! < -0.5 && " — over; the manager will need to move money in"}</>}
            {amountEx > 0 && po && po.budget_code === budgetCode && <> · this bill nets off the PO's commitment, the bucket's left stays about the same</>}
          </div>
        )}
        {isProject && budgetCode && sel?.unbudgeted && (
          <div className="text-[11px] text-warn mt-1 inline-flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0"/>No bucket on this job for {budgetCode} — the bill will show as "money on a code with no budget" until the manager moves money in. Pick a bucket above if one fits.</div>
        )}
        {isProject && budgetCode && !sel && buckets.length > 0 && budgetCode.toUpperCase().startsWith("PX") && (
          <div className="text-[11px] text-warn mt-1 inline-flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0"/>No bucket on this job for {budgetCode}. Pick a bucket above if one fits.</div>
        )}
        {overhead && (
          <div className="text-[11px] text-danger mt-1 inline-flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0"/>{budgetCode} is an overhead code — on a project bill it never lands in a bucket. Fuel for a job goes on the job's plant or travel bucket (keep the FTC class).</div>
        )}
      </div>
    </div>
  );
}

function tidy(s: string | null | undefined) { return (s ?? "").replace(/â€”/g, "—"); }
